import { z } from "zod";

/**
 * `CHEF-SELF-VALIDATION` R1 (D-1029-2, the founder's choices of 29 October): **what the chef who instructs a dossier herself may do with it,
 * said by the server, never computed by a browser.** The composer's answer (`GET` and `PUT /api/v1/bank/files/{id}/offer`) and the decision
 * answer (`GET /api/v1/bank/decisions/{id}`) carry `ownFile`: `null` for anyone who does not hold the file as the chef, else the ROUTE (a closed
 * enum) and the SENTENCE the server serves with it, so the screen binds both and the server's refusal stays the authority.
 *
 * - `validateAndSend`: the setting is ON, no derogation, within her delegation. One gesture « Valider et envoyer au client »
 *   (`POST /api/v1/bank/files/{id}/offer/validate-and-send`). No sentence.
 * - `aboveDelegation`: ON, no derogation, the amount above her delegation. She validates and sends in the same gesture; the comité decides
 *   AFTER the client accepts. The approved sentence.
 * - `sendToCommittee` (Round 2, `P1-LND-086`): ON and ANY gap stands on the file (chef-level or comité-level; alone or with an amount above her
 *   delegation: the derogation wins and one sitting covers both, frame 192). The file goes to the comité BEFORE the offer: the gesture
 *   « Envoyer au comité de crédit » (`POST /api/v1/bank/files/{id}/offer/send-to-committee`), the approved sentence « Ce dossier porte une
 *   dérogation : il part au comité de crédit. ».
 * - `derogationRefused`: Round 1's interim route (the same case refused in the words « réaffectez-le à un analyste »). RETIRED by Round 2: the
 *   server no longer serves it; the value stays in the closed enum, both texts kept, so a client built on Round 1 still parses every answer.
 * - `ownFileRefused`: the setting is OFF (or the bank has no policy). The approved refusal.
 *
 * `notice` is « Vous avez instruit ce dossier. », the decision screen's own-file box (frames 194 to 196).
 */
export const OWN_FILE_ROUTES = [
  "validateAndSend",
  "aboveDelegation",
  "sendToCommittee",
  "derogationRefused",
  "ownFileRefused",
] as const;

export type OwnFileRoute = (typeof OWN_FILE_ROUTES)[number];

export const ownFileSchema = z.strictObject({
  route: z.enum(OWN_FILE_ROUTES),
  sentence: z.string().nullable(),
  notice: z.string(),
});

export type OwnFile = z.infer<typeof ownFileSchema>;
